import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import {
  VoiceAuditionRequest,
  VoiceAuditionResult,
  VoiceCatalogListItem,
  VoiceSelectRequest,
  VoiceSelectResult,
} from './voice-catalog.types';
import { FluxVoice, FluxVoicePreviewResult } from '../flux-voices';
import { RealtimeVoice, RealtimeVoicePreviewResult } from '../realtime-voices';

@Injectable({ providedIn: 'root' })
export class VoiceCatalogService {
  private readonly http = inject(HttpClient);

  /**
   * Candidate voices for a bot. AIVA1 bots (or any bot with no AIVA2 flow set)
   * get an empty list back — enforced server-side, not by this UI — so this
   * call is safe to make unconditionally for any voice bot.
   */
  listCatalog(botId: string): Observable<VoiceCatalogListItem[]> {
    return this.http.get<VoiceCatalogListItem[]>(
      `${environment.engineUrl}/voice-bot/voice-catalog?botId=${encodeURIComponent(botId)}`
    );
  }

  audition(botId: string, voiceKey: string, body: VoiceAuditionRequest): Observable<VoiceAuditionResult> {
    return this.http.post<VoiceAuditionResult>(
      `${environment.engineUrl}/voice-bot/${encodeURIComponent(botId)}/voice-catalog/${encodeURIComponent(
        voiceKey
      )}/audition`,
      body
    );
  }

  /** Saves this candidate as the bot's real TTS voice for `body.language` — what live calls use next. */
  select(botId: string, voiceKey: string, body: VoiceSelectRequest): Observable<VoiceSelectResult> {
    return this.http.post<VoiceSelectResult>(
      `${environment.engineUrl}/voice-bot/${encodeURIComponent(botId)}/voice-catalog/${encodeURIComponent(voiceKey)}/select`,
      body
    );
  }

  /** The fixed preview line rendered in a Flux voice (inline MP3). */
  fluxVoicePreview(voice: FluxVoice): Observable<FluxVoicePreviewResult> {
    return this.http.post<FluxVoicePreviewResult>(`${environment.engineUrl}/voice-bot/flux-voice-preview`, { voice });
  }

  /** The fixed preview line rendered in an OpenAI Realtime voice (inline MP3). */
  realtimeVoicePreview(voice: RealtimeVoice): Observable<RealtimeVoicePreviewResult> {
    return this.http.post<RealtimeVoicePreviewResult>(`${environment.engineUrl}/voice-bot/realtime-voice-preview`, {
      voice,
    });
  }
}
